<script lang="ts">
  import { Pagination, type PaginationProps } from '@stickerapp-org/athena/Pagination';
  import { HeadingLarge, Text } from '@stickerapp-org/athena/Text';
  import { Link } from '@stickerapp-org/athena/Link';
  import { GridItem, Grid } from '@athena-lib/Grid';
  import { BuyStickerCard } from '@athena-lib/BuyStickerCard';
  import Stack from '@athena-lib/Stack/Stack.svelte';
  import { getLocalizationContext } from '@athena-lib/i18n';
  import Image from '@athena-lib/Image/Image.svelte';
  import StickerShopLayout from './shared/StickerShopLayout.svelte';
  import type { DesignerSourceData } from '$lib/server/external/types';
  import { getBuyStickerCardProps } from '$lib/utilities/getBuyStickerCardProps';
  import { getShoppingCart } from '$lib/shopping-cart.svelte';
  import { trackCategoryItemViewList } from '$lib/gtm/ecommerce-tracking';
  import { getCheckoutSdk } from '$lib/checkout.svelte';

  const {
    data,
  }: {
    data: DesignerSourceData;
  } = $props();

  const stickerShopMeta = $derived(data.stickerShopMeta);

  if (!data) {
    throw new Error('Invalid source data');
  }
  const figures = $derived(data.figureData.data ?? []);
  const paginationMeta = $derived(data.figureData.meta);
  const showPagination = $derived(paginationMeta.total_pages > 1);

  let paginationProps: PaginationProps | undefined = $derived(
    stickerShopMeta.stickerShopUrls
      ? {
          pageCount: paginationMeta.total_pages,
          current: paginationMeta.current_page,
          baseUrl: `${stickerShopMeta.stickerShopUrls.designers}/${data.slug}`,
          queryParamName: 'p',
        }
      : undefined,
  );

  const refreshCart = getShoppingCart().refreshCart;

  const localizationContext = getLocalizationContext();
  const checkoutSdk = $derived(getCheckoutSdk());

  trackCategoryItemViewList({
    figureData: data.figureData,
    stickerShopMeta: data.stickerShopMeta,
    localization: localizationContext,
  });
</script>

<StickerShopLayout hideCategories>
  <div class="mb-10 flex flex-col items-center gap-10 md:flex-row">
    <Image loading="lazy" class="max-h-44 max-w-44 rounded-full" src={data?.avatar} alt={''} />
    <div class="flex flex-col gap-2">
      <HeadingLarge>{data?.name}</HeadingLarge>
      {#if data?.about}
        <Text>{data?.about}</Text>
      {/if}
      {#if data?.link}
        <Link href={data.link} isExternal>
          {data.link}
        </Link>
      {/if}
    </div>
  </div>
  <Stack>
    <Grid columns={{ lg: 4, md: 3 }}>
      {#each figures as figure}
        <GridItem>
          <BuyStickerCard
            {...getBuyStickerCardProps(
              figure,
              stickerShopMeta,
              localizationContext,
              refreshCart,
              checkoutSdk,
            )}
          ></BuyStickerCard>
        </GridItem>
      {/each}
    </Grid>
    {#if showPagination && paginationProps}
      <Pagination class="pt-10" {...paginationProps}></Pagination>
    {/if}
  </Stack>
</StickerShopLayout>
